import { z } from 'zod';
import { nodeAddressSchema } from './addresses.js';
import { messageKey } from './messages.js';
/**
 * Типы узлов из §7.4. Список закрытый: по нему подбирается иконка на карте,
 * и незнакомый тип нарисовать нечем.
 *
 * `cloud` — облачный сервер — в §7.4 нет, добавлен по требованию заказчика.
 * Ведёт себя как `pc` и `server` (консоль, терминал) и отличается только
 * глифом: арендованная машина у провайдера — всё та же машина, но на схеме
 * её надо отличать от той, что стоит в стойке.
 *
 * `nvr` — видеорегистратор — тоже сверх §7.4 и по той же просьбе: одно
 * устройство со многими каналами, каждый из которых на стене смотрится
 * как камера (ADR 037).
 */
export const NODE_KINDS = [
    'pc',
    'server',
    'cloud',
    'camera',
    'nvr',
    'switch',
    'router',
    'printer',
    'ap',
    'nas',
    'ups',
    'phone',
    'label',
    'other',
];
export const nodeKindSchema = z.enum(NODE_KINDS);
/** Стиль линии связи (§7.4). */
export const linkStyleSchema = z.enum(['solid', 'dashed', 'dotted']);
/**
 * Способ прокладки линии. В §7.4 такого поля нет — добавлено по требованию
 * заказчика.
 *
 * Стиль и маршрут разведены намеренно: пунктирная связь может быть и ломаной,
 * и кривой, а совмещение их в одно поле дало бы девять значений вместо трёх
 * и трёх.
 */
export const linkRoutingSchema = z.enum(['step', 'bezier', 'straight']);
/**
 * Сторона узла, к которой подключена связь.
 *
 * Хранится, потому что иначе после перезагрузки линия привязывается к стороне
 * по умолчанию: с какой стороны её тянули, из данных не следует, а на схеме
 * это разница между аккуратной магистралью и путаницей.
 */
export const linkSideSchema = z.enum(['top', 'right', 'bottom', 'left']);
export const LINK_SIDES = linkSideSchema.options;
/**
 * Сколько точек крепления помещается на одной стороне узла.
 *
 * Одна — как было всегда; больше нужно там, где в узел приходит несколько
 * линий с одной стороны: у коммутатора, к которому подключены четыре машины
 * снизу, все четыре сходились в одну точку, и разобрать на схеме, что куда
 * идёт, было нельзя.
 *
 * Верхний предел — шесть. Он не круглый и взят не с потолка: карточка узла
 * шириной 168 px, и шесть точек на этой стороне стоят через 24 px — это
 * последнее расстояние, на котором в точку ещё попадают мышью, не целясь
 * в соседнюю. Боковые стороны короче, и там шесть уже тесно, но запрещать
 * их отдельно значило бы объяснять человеку разницу, которой он не просил.
 */
export const MAX_LINK_POINTS = 6;
const linkPointCountSchema = z.number().int().min(1).max(MAX_LINK_POINTS);
/**
 * Число точек крепления по каждой стороне узла.
 *
 * Все четыре стороны заданы всегда: сторона без числа означала бы «сколько-то»,
 * и разбираться, сколько именно, пришлось бы и карте, и правилу удаления
 * связей. Умолчание — по одной, то есть ровно то, как карта вела себя раньше.
 */
export const linkPointsSchema = z.object({
    top: linkPointCountSchema,
    right: linkPointCountSchema,
    bottom: linkPointCountSchema,
    left: linkPointCountSchema,
});
export const DEFAULT_LINK_POINTS = { top: 1, right: 1, bottom: 1, left: 1 };
/**
 * Номер точки на стороне, считая от начала стороны, с нуля.
 *
 * `null` у старых связей значит «единственная точка», то есть ноль: до этой
 * доработки точка на стороне была одна, и переписывать ради номера всё, что
 * уже нарисовано, незачем.
 */
export const linkPointIndexSchema = z
    .number()
    .int()
    .min(0)
    .max(MAX_LINK_POINTS - 1);
/**
 * Где на стороне стоит точка с этим номером — доля от начала стороны.
 *
 * Точки распределяются равномерно **с отступами от углов**: `(i + 1) / (n + 1)`,
 * а не `i / (n - 1)`. При одной точке это даёт середину — то есть ровно то,
 * где точка стояла до сих пор, и ни одна нарисованная схема от доработки
 * не поедет. Второй вариант при одной точке делит на ноль, а при двух ставит
 * точки в самые углы, где они сливаются с точками соседних сторон.
 */
export function linkPointOffset(index, count) {
    const points = Math.max(1, count);
    const at = Math.min(Math.max(index, 0), points - 1);
    return (at + 1) / (points + 1);
}
/**
 * Цвет в виде `#rgb` или `#rrggbb`. Значение уходит прямо в атрибут стиля
 * на карте, поэтому произвольную строку принимать нельзя.
 */
const colorSchema = z
    .string()
    .regex(/^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/, messageKey('validation.node.color'));
/**
 * Координата на карте. Ограничение конечным числом обязательно: `z.number()`
 * пропускает NaN и Infinity, а такая координата в `real` уводит узел
 * в никуда — вернуть его мышью уже нельзя.
 */
const coordinateSchema = z.number().finite().min(-1_000_000).max(1_000_000);
export const nodeSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    layerId: z.uuid(),
    groupId: z.uuid().nullable(),
    kind: nodeKindSchema,
    name: z.string(),
    address: z.string().nullable(),
    x: z.number(),
    y: z.number(),
    icon: z.string().nullable(),
    notes: z.string().nullable(),
    metadata: z.record(z.string(), z.unknown()),
    /**
     * Поля метаданных, показываемые на карточке узла.
     *
     * Отдельный список, а не признак внутри самих метаданных: метаданные —
     * произвольные данные заказчика, и служебные пометки в них однажды
     * столкнутся с настоящим полем. По умолчанию список пуст: инвентарный
     * номер и MAC нужны в карточке узла, а не на схеме.
     */
    pinnedMetadata: z.array(z.string()),
    /** Сколько точек крепления связей на каждой стороне. По умолчанию по одной. */
    linkPoints: linkPointsSchema,
    createdAt: z.iso.datetime(),
});
export const createNodeSchema = z.object({
    kind: nodeKindSchema,
    /** Слой карты. Не задан — узел ложится на первый слой площадки. */
    layerId: z.uuid().optional(),
    name: z.string().trim().min(1).max(200),
    address: z.string().trim().max(255).nullable().optional(),
    x: coordinateSchema,
    y: coordinateSchema,
    groupId: z.uuid().nullable().optional(),
    icon: z.string().trim().max(100).nullable().optional(),
    notes: z.string().trim().max(4000).nullable().optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
    // Больше четырёх строк карточка не вмещает, не перекрывая соседей.
    pinnedMetadata: z.array(z.string().trim().min(1).max(64)).max(4).optional(),
    linkPoints: linkPointsSchema.optional(),
});
export const updateNodeSchema = createNodeSchema.partial();
/**
 * Массовое обновление координат при перетаскивании (§8.2).
 * Предел в 1000 позиций совпадает с разумным размером карты: больше за один
 * жест не перетащить, а неограниченный список — это неограниченный запрос.
 */
export const updatePositionsSchema = z
    .array(z.object({
    id: z.uuid(),
    x: coordinateSchema,
    y: coordinateSchema,
    groupId: z.uuid().nullable().optional(),
}))
    .min(1)
    .max(1000);
export const nodeGroupSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    layerId: z.uuid(),
    parentId: z.uuid().nullable(),
    name: z.string(),
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    color: z.string().nullable(),
    /** Заливка рамки; `null` — фон по умолчанию, как у остальной карты. */
    fill: z.string().nullable(),
    /**
     * Насколько заливка непрозрачна: 0 — совсем прозрачная, 1 — сплошная.
     *
     * Отдельно от цвета, потому что цвет выбирают один раз, а плотность
     * подбирают под карту: одна и та же заливка на пустой схеме и на схеме,
     * где под рамкой лежат связи, требует разной насыщенности.
     */
    fillOpacity: z.number(),
    collapsed: z.boolean(),
});
export const createNodeGroupSchema = z.object({
    name: z.string().trim().min(1).max(200),
    /** Слой карты. Не задан — рамка ложится на первый слой площадки. */
    layerId: z.uuid().optional(),
    parentId: z.uuid().nullable().optional(),
    x: coordinateSchema.optional(),
    y: coordinateSchema.optional(),
    width: z.number().finite().min(40).max(100_000).optional(),
    height: z.number().finite().min(40).max(100_000).optional(),
    color: colorSchema.nullable().optional(),
    fill: colorSchema.nullable().optional(),
    fillOpacity: z.number().min(0).max(1).optional(),
    collapsed: z.boolean().optional(),
});
export const updateNodeGroupSchema = createNodeGroupSchema.partial();
export const linkSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    sourceId: z.uuid(),
    targetId: z.uuid(),
    label: z.string().nullable(),
    style: linkStyleSchema,
    routing: linkRoutingSchema,
    /** Стороны подключения; `null` — сторона по умолчанию. */
    sourceSide: linkSideSchema.nullable(),
    targetSide: linkSideSchema.nullable(),
    /** Номер точки на стороне, с нуля; `null` — единственная точка. */
    sourceIndex: linkPointIndexSchema.nullable(),
    targetIndex: linkPointIndexSchema.nullable(),
    color: z.string().nullable(),
    waypoints: z.array(z.object({ x: z.number(), y: z.number() })),
});
export const createLinkSchema = z
    .object({
    sourceId: z.uuid(),
    targetId: z.uuid(),
    label: z.string().trim().max(200).nullable().optional(),
    style: linkStyleSchema.optional(),
    routing: linkRoutingSchema.optional(),
    sourceSide: linkSideSchema.nullable().optional(),
    targetSide: linkSideSchema.nullable().optional(),
    sourceIndex: linkPointIndexSchema.nullable().optional(),
    targetIndex: linkPointIndexSchema.nullable().optional(),
    color: colorSchema.nullable().optional(),
    waypoints: z
        .array(z.object({ x: coordinateSchema, y: coordinateSchema }))
        .max(50)
        .optional(),
})
    // Петля из узла в себя рисуется как точка и ничего не означает.
    .refine((link) => link.sourceId !== link.targetId, {
    message: messageKey('validation.node.selfLink'),
    path: ['targetId'],
});
/** Концы связи не меняются: проще удалить и нарисовать заново. */
export const updateLinkSchema = z.object({
    label: z.string().trim().max(200).nullable().optional(),
    style: linkStyleSchema.optional(),
    routing: linkRoutingSchema.optional(),
    sourceSide: linkSideSchema.nullable().optional(),
    targetSide: linkSideSchema.nullable().optional(),
    sourceIndex: linkPointIndexSchema.nullable().optional(),
    targetIndex: linkPointIndexSchema.nullable().optional(),
    color: colorSchema.nullable().optional(),
    waypoints: z
        .array(z.object({ x: coordinateSchema, y: coordinateSchema }))
        .max(50)
        .optional(),
});
/**
 * Вся карта площадки одним ответом (§8.2). Отдельные запросы за узлами,
 * связями и группами дали бы карту, собранную из несогласованных снимков.
 */
/**
 * Слой карты площадки.
 *
 * Слой — измерение видимости, а не отдельная карта: координатное
 * пространство у площадки одно, и слои складываются друг с другом.
 * Отсюда следует главное: связь между узлами разных слоёв — обычная связь,
 * а не особый случай, и экспорт берёт схему площадки целиком.
 *
 * Какие слои видны сейчас, здесь не хранится: это предпочтение зрителя,
 * как и видимость точек сетки, и живёт оно в браузере. Общий состав слоёв
 * и порядок, в котором они ложатся друг на друга, — здесь.
 */
export const mapLayerSchema = z.object({
    id: z.uuid(),
    siteId: z.uuid(),
    name: z.string(),
    /** Больший номер ложится поверх. */
    position: z.number().int(),
});
export const createMapLayerSchema = z.object({
    name: z.string().trim().min(1, messageKey('validation.name.required')).max(100),
    position: z.number().int().min(0).max(1000).optional(),
});
export const updateMapLayerSchema = createMapLayerSchema.partial();
export const topologySchema = z.object({
    nodes: z.array(nodeSchema),
    links: z.array(linkSchema),
    groups: z.array(nodeGroupSchema),
    /**
     * Слои площадки. Приходят вместе со схемой, а не отдельным запросом:
     * без них карту не нарисовать — у каждого узла и каждой рамки есть слой,
     * и показывать их до того, как известен состав слоёв, нечем.
     */
    layers: z.array(mapLayerSchema),
    /**
     * Дополнительные адреса узлов площадки (ADR 035). Здесь же, а не отдельным
     * запросом, по той же причине, что и слои: карточка узла рисует их точками,
     * и второй запрос означал бы карту, которая мгновение показывает узлы
     * без адресов, а потом дёргается.
     */
    addresses: z.array(nodeAddressSchema),
});
/**
 * Связи, которые потеряют точку крепления, если у узла станет меньше точек.
 *
 * Считается и на сервере, и в интерфейсе — по одному правилу, потому что
 * расхождение здесь стоит дорого: интерфейс предупредит про две связи,
 * сервер удалит три, и человек узнает об этом по пропавшей линии.
 *
 * Проверяются оба конца: узел бывает и источником, и целью, и связь между
 * двумя узлами, у которых обоих убрали точки, попадает под правило дважды —
 * поэтому возвращается набор, а не список.
 *
 * Сторона `null` означает точку по умолчанию, а она есть всегда: убрать
 * последнюю точку со стороны нельзя, минимум — одна.
 */
export function linksLosingPoint(nodeId, points, links) {
    const lost = new Set();
    const beyond = (side, index) => side !== null && (index ?? 0) >= points[side];
    for (const link of links) {
        if (link.sourceId === nodeId && beyond(link.sourceSide, link.sourceIndex)) {
            lost.add(link.id);
            continue;
        }
        if (link.targetId === nodeId && beyond(link.targetSide, link.targetIndex)) {
            lost.add(link.id);
        }
    }
    return lost;
}
//# sourceMappingURL=nodes.js.map